import { useState, useEffect, useMemo } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-6 — AssinaturaModal (Add | Edit) com preview live.

const FREQ_OPTIONS = [
  { value: "monthly", label: "Mensal" },
  { value: "annual",  label: "Anual" },
  { value: "weekly",  label: "Semanal" },
  { value: "custom",  label: "Custom" },
];

const STATUS_OPTIONS = [
  { value: "active",   label: "Ativa" },
  { value: "paused",   label: "Pausada" },
  { value: "canceled", label: "Cancelada" },
];

const PAYMENT_METHODS = [
  "Pix", "Crédito", "Débito", "Boleto", "TED", "DOC", "Transferência", "Dinheiro", "Other",
];

function _toCents(v) {
  if (v == null || v === "") return null;
  const s = String(v).replace(/\./g, "").replace(",", ".").replace(/[^0-9.\-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function _formatBRL(v) {
  if (v == null) return "";
  return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function ValidationMsg({ children }) {
  return <p className="text-micro text-status-error mt-1" role="alert">{children}</p>;
}

function _nextBillingPreview(billingDay, frequency) {
  const day = parseInt(billingDay, 10);
  if (!Number.isFinite(day) || day < 1 || day > 31) return null;
  const today = new Date();
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const safe = Math.min(day, lastOfMonth);
  let candidate = new Date(today.getFullYear(), today.getMonth(), safe);
  if (candidate < today) {
    const lastNext = new Date(today.getFullYear(), today.getMonth() + 2, 0).getDate();
    candidate = new Date(today.getFullYear(), today.getMonth() + 1, Math.min(day, lastNext));
  }
  return candidate;
}

export default function AssinaturaModal({
  open,
  onOpenChange,
  subscription,           // null = create
  onCreate,
  onUpdate,
  knownCategories = [],
  knownAccounts = [],
}) {
  const isEditing = Boolean(subscription);

  const [name, setName] = useState("");
  const [merchant, setMerchant] = useState("");
  const [amount, setAmount] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [billingDay, setBillingDay] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Crédito");
  const [accountId, setAccountId] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("active");
  const [notes, setNotes] = useState("");
  const [showCatSuggestions, setShowCatSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      setName(subscription?.name || "");
      setMerchant(subscription?.merchant || "");
      setAmount(subscription?.amount != null ? _formatBRL(subscription.amount) : "");
      setFrequency(subscription?.frequency || "monthly");
      setBillingDay(subscription?.billing_day != null ? String(subscription.billing_day) : "");
      setPaymentMethod(subscription?.payment_method || "Crédito");
      setAccountId(subscription?.account_id || "");
      setCategory(subscription?.category || "");
      setStatus(subscription?.status || "active");
      setNotes(subscription?.notes || "");
      setSaving(false);
      setTouched({});
      setShowCatSuggestions(false);
    }
  }, [open, subscription]);

  const errors = useMemo(() => {
    const out = {};
    const nm = (name || "").trim();
    if (!nm) out.name = "Informe um nome";
    else if (nm.length > 100) out.name = "Máx 100 caracteres";

    const mc = (merchant || "").trim();
    if (!mc) out.merchant = "Informe o merchant";
    else if (mc.length > 100) out.merchant = "Máx 100 caracteres";

    const amt = _toCents(amount);
    if (amt == null || amt <= 0) out.amount = "Valor > 0";

    const day = parseInt(billingDay, 10);
    if (!Number.isFinite(day) || day < 1 || day > 31) out.billingDay = "Dia 1-31";

    if (notes && notes.length > 500) out.notes = "Máx 500 caracteres";

    return out;
  }, [name, merchant, amount, billingDay, notes]);

  const hasErrors = Object.keys(errors).length > 0;
  const showErr = (k) => touched[k] && errors[k];

  // Preview live
  const preview = useMemo(() => {
    const amt = _toCents(amount) || 0;
    let monthly = 0, annual = 0;
    if (frequency === "monthly") { monthly = amt; annual = amt * 12; }
    else if (frequency === "annual") { monthly = amt / 12; annual = amt; }
    else if (frequency === "weekly") { monthly = amt * 4.345; annual = amt * 52.18; }
    else { monthly = amt; annual = amt * 12; }

    const nextDt = _nextBillingPreview(billingDay, frequency);
    return {
      monthly: Math.round(monthly * 100) / 100,
      annual: Math.round(annual * 100) / 100,
      nextDate: nextDt,
    };
  }, [amount, frequency, billingDay]);

  const filteredCategories = useMemo(() => {
    if (!category || !showCatSuggestions) return [];
    const q = category.toLowerCase();
    return (knownCategories || []).filter(c => c.toLowerCase().includes(q) && c.toLowerCase() !== q).slice(0, 5);
  }, [category, showCatSuggestions, knownCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, merchant: true, amount: true, billingDay: true, notes: true });
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        merchant: merchant.trim(),
        amount: _toCents(amount),
        frequency,
        billing_day: parseInt(billingDay, 10),
        payment_method: paymentMethod || "Crédito",
        account_id: accountId || null,
        category: category.trim() || null,
        status: isEditing ? status : "active",
        notes: notes.trim() || null,
      };
      if (isEditing) {
        await onUpdate(subscription.id, payload);
        toast.success("Assinatura atualizada");
      } else {
        await onCreate(payload);
        toast.success("Assinatura adicionada");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Falha ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface-overlay border border-subtle max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="sub-modal"
      >
        <DialogHeader>
          <DialogTitle data-testid="sub-modal-title">
            {isEditing ? `Editar ${subscription?.name || ""}` : "Nova assinatura"}
          </DialogTitle>
          <DialogDescription className="text-foreground-muted text-caption">
            Cadastre serviços recorrentes pra acompanhar custos e vencimentos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-3.5 py-2">
            {/* Nome + Merchant */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="sub-name" className="block text-caption font-medium text-foreground-muted mb-1">
                  Nome
                </label>
                <Input
                  id="sub-name"
                  type="text"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, name: true }))}
                  placeholder="Ex: Netflix"
                  maxLength={120}
                  aria-invalid={Boolean(showErr("name"))}
                  data-testid="sub-modal-name"
                />
                {showErr("name") && <ValidationMsg>{errors.name}</ValidationMsg>}
              </div>
              <div>
                <label htmlFor="sub-merchant" className="block text-caption font-medium text-foreground-muted mb-1">
                  Merchant
                </label>
                <Input
                  id="sub-merchant"
                  type="text"
                  value={merchant}
                  onChange={(e) => setMerchant(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, merchant: true }))}
                  placeholder="NETFLIX.COM"
                  maxLength={120}
                  aria-invalid={Boolean(showErr("merchant"))}
                  data-testid="sub-modal-merchant"
                />
                {showErr("merchant") && <ValidationMsg>{errors.merchant}</ValidationMsg>}
              </div>
            </div>

            {/* Valor + Frequência */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="sub-amount" className="block text-caption font-medium text-foreground-muted mb-1">
                  Valor (R$)
                </label>
                <Input
                  id="sub-amount"
                  type="text"
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, amount: true }))}
                  placeholder="39,90"
                  aria-invalid={Boolean(showErr("amount"))}
                  data-testid="sub-modal-amount"
                />
                {showErr("amount") && <ValidationMsg>{errors.amount}</ValidationMsg>}
              </div>
              <div>
                <label htmlFor="sub-freq" className="block text-caption font-medium text-foreground-muted mb-1">
                  Frequência
                </label>
                <div className="relative">
                  <select
                    id="sub-freq"
                    value={frequency}
                    onChange={(e) => setFrequency(e.target.value)}
                    className="w-full appearance-none px-3 py-2 pr-8 bg-surface-card-hover border border-subtle rounded-md text-foreground text-caption focus:outline-none focus:ring-1 focus:ring-brand-cyan focus:border-brand-cyan/40"
                    data-testid="sub-modal-frequency"
                  >
                    {FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
                </div>
              </div>
            </div>

            {/* billing_day + payment_method */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="sub-day" className="block text-caption font-medium text-foreground-muted mb-1">
                  Dia de cobrança
                </label>
                <Input
                  id="sub-day"
                  type="number"
                  min="1"
                  max="31"
                  value={billingDay}
                  onChange={(e) => setBillingDay(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, billingDay: true }))}
                  placeholder="15"
                  aria-invalid={Boolean(showErr("billingDay"))}
                  data-testid="sub-modal-billing-day"
                />
                {showErr("billingDay") && <ValidationMsg>{errors.billingDay}</ValidationMsg>}
              </div>
              <div>
                <label htmlFor="sub-pm" className="block text-caption font-medium text-foreground-muted mb-1">
                  Forma de pagamento
                </label>
                <div className="relative">
                  <select
                    id="sub-pm"
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full appearance-none px-3 py-2 pr-8 bg-surface-card-hover border border-subtle rounded-md text-foreground text-caption focus:outline-none focus:ring-1 focus:ring-brand-cyan focus:border-brand-cyan/40"
                    data-testid="sub-modal-payment"
                  >
                    {PAYMENT_METHODS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
                </div>
              </div>
            </div>

            {/* Conta + Categoria */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="sub-acc" className="block text-caption font-medium text-foreground-muted mb-1">
                  Conta/cartão (opcional)
                </label>
                <div className="relative">
                  <select
                    id="sub-acc"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value)}
                    className="w-full appearance-none px-3 py-2 pr-8 bg-surface-card-hover border border-subtle rounded-md text-foreground text-caption focus:outline-none focus:ring-1 focus:ring-brand-cyan focus:border-brand-cyan/40"
                    data-testid="sub-modal-account"
                  >
                    <option value="">Sem conta vinculada</option>
                    {(knownAccounts || []).map(a => (
                      <option key={a.id} value={a.id}>{a.name}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
                </div>
              </div>
              <div className="relative">
                <label htmlFor="sub-cat" className="block text-caption font-medium text-foreground-muted mb-1">
                  Categoria (opcional)
                </label>
                <Input
                  id="sub-cat"
                  type="text"
                  value={category}
                  onChange={(e) => { setCategory(e.target.value); setShowCatSuggestions(true); }}
                  onFocus={() => setShowCatSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowCatSuggestions(false), 150)}
                  placeholder="Streaming, Saúde..."
                  maxLength={50}
                  autoComplete="off"
                  data-testid="sub-modal-category"
                />
                {filteredCategories.length > 0 && (
                  <div
                    className="absolute z-10 left-0 right-0 mt-0.5 bg-surface-overlay border border-subtle rounded-md shadow-lg overflow-hidden"
                    role="listbox"
                  >
                    {filteredCategories.map(s => (
                      <button
                        key={s}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setCategory(s); setShowCatSuggestions(false); }}
                        className="w-full text-left px-3 py-1.5 text-caption text-foreground-muted hover:bg-surface-card-hover hover:text-foreground"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Status (só em edit mode) */}
            {isEditing && (
              <div>
                <label htmlFor="sub-status" className="block text-caption font-medium text-foreground-muted mb-1">
                  Status
                </label>
                <div className="relative">
                  <select
                    id="sub-status"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="w-full appearance-none px-3 py-2 pr-8 bg-surface-card-hover border border-subtle rounded-md text-foreground text-caption focus:outline-none focus:ring-1 focus:ring-brand-cyan focus:border-brand-cyan/40"
                    data-testid="sub-modal-status"
                  >
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label htmlFor="sub-notes" className="block text-caption font-medium text-foreground-muted mb-1">
                Notas (opcional)
              </label>
              <textarea
                id="sub-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Observações..."
                className="w-full px-3 py-2 bg-surface-card-hover border border-subtle rounded-md text-foreground text-caption focus:outline-none focus:ring-1 focus:ring-brand-cyan focus:border-brand-cyan/40 resize-none"
                data-testid="sub-modal-notes"
              />
              {showErr("notes") && <ValidationMsg>{errors.notes}</ValidationMsg>}
            </div>

            {/* Preview live */}
            <div className="bg-surface-card-hover/40 border border-subtle rounded-md p-3 space-y-1.5" data-testid="sub-modal-preview">
              <p className="text-micro text-foreground-muted uppercase font-semibold mb-1.5" style={{ letterSpacing: "0.10em" }}>
                Preview
              </p>
              <div className="flex items-center justify-between text-caption">
                <span className="text-foreground-muted">Custo mensal normalizado</span>
                <span className="font-mono tabular-nums text-foreground">{fmtBRL(preview.monthly)}</span>
              </div>
              <div className="flex items-center justify-between text-caption">
                <span className="text-foreground-muted">Custo anual normalizado</span>
                <span className="font-mono tabular-nums text-foreground">{fmtBRL(preview.annual)}</span>
              </div>
              <div className="flex items-center justify-between text-caption">
                <span className="text-foreground-muted">Próxima cobrança</span>
                <span className="text-foreground">
                  {preview.nextDate
                    ? `${String(preview.nextDate.getDate()).padStart(2, "0")}/${String(preview.nextDate.getMonth()+1).padStart(2,"0")}/${preview.nextDate.getFullYear()}`
                    : "—"}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving} data-testid="sub-modal-cancel">
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={saving || hasErrors} data-testid="sub-modal-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEditing ? "Salvar" : "Adicionar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
